import React, { useRef, useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';

interface MapGridProps {
  gridSize: number;
  zoom: number;
  pan: { x: number; y: number };
  warehouseWidth: number;
  warehouseHeight: number;
  showGrid: boolean;
  onZoomChange: (zoom: number, newPan?: { x: number; y: number }) => void;
  onPanChange: (pan: { x: number; y: number }) => void;
  onMouseDown: (e: React.MouseEvent, gridX: number, gridY: number) => void;
  onMouseMove: (e: React.MouseEvent, gridX: number, gridY: number) => void;
  onMouseUp: (e: React.MouseEvent) => void;
  children: React.ReactNode;
  /** Bilinçli minimum/maksimum zoom */
  minZoom?: number;
  maxZoom?: number;
}

/**
 * Harita çalışma alanı. Etkileşim modeli:
 * - Tekerlek           → cursor altında yakınlaştır/uzaklaştır
 * - Sağ tık + sürükle  → kaydır (pan)
 * - Orta tık + sürükle → kaydır
 * - Alt + sol sürükle  → kaydır
 * - Boşluk + sol sürükle → kaydır
 *
 * Pan, haritayı viewport içinden kaybolmayacak şekilde clamp eder.
 */
const MapGrid: React.FC<MapGridProps> = ({
  gridSize, zoom, pan, warehouseWidth, warehouseHeight, showGrid,
  onZoomChange, onPanChange, onMouseDown, onMouseMove, onMouseUp, children,
  minZoom = 0.1, maxZoom = 3,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0 });
  const panOrigin = useRef({ x: 0, y: 0 });
  const spaceHeld = useRef(false);
  const livePanRef = useRef(pan);
  const [livePan, setLivePan] = useState(pan);
  const [isHoverPanZone, setIsHoverPanZone] = useState(false);
  const [isActivePan, setIsActivePan] = useState(false);

  // Ref'ler: hızlı arka arkaya gelen wheel olaylarında state güncellemesi
  // yetişmeyebiliyor; refleri senkron güncelleyerek "ping/zıplama" hissini kırıyoruz.
  const zoomRef = useRef(zoom);
  const panRef = useRef(pan);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { panRef.current = pan; }, [pan]);
  useEffect(() => {
    // Dışarıdan (fit-to-screen vb.) pan değişirse iç görünümü senkronla.
    // Kullanıcı sürüklerken dış değişiklikle fight etmemek için drag sırasında dokunma.
    if (!isPanning.current) {
      livePanRef.current = pan;
      setLivePan(pan);
    }
  }, [pan]);

  const cellPx = gridSize * zoom;
  const totalW = warehouseWidth * cellPx;
  const totalH = warehouseHeight * cellPx;
  // "Dünya" katmanı SABİT temel boyutta çizilir; zoom/pan CSS transform (GPU) ile uygulanır.
  // Böylece zoom/pan sırasında içindeki binlerce öğe yeniden layout/paint edilmez.
  const baseW = warehouseWidth * gridSize;
  const baseH = warehouseHeight * gridSize;

  const clampPan = useCallback((nextPan: { x: number; y: number }, contentW: number, contentH: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return nextPan;
    const margin = 80; // harita en az 80px görünür kalsın
    const clampAxis = (value: number, content: number, viewport: number) => {
      if (content + margin * 2 <= viewport) {
        // İçerik viewport'tan küçükse de pan'i sabitleme.
        // Merkez etrafında kontrollü serbest alan tanıyarak sağ/sol ve yukarı/aşağı sürüklemeyi aç.
        const center = (viewport - content) / 2;
        const freeRange = Math.max(120, Math.floor(viewport * 0.35));
        const min = center - freeRange;
        const max = center + freeRange;
        return Math.max(min, Math.min(max, value));
      }
      const min = viewport - content - margin;
      const max = margin;
      return Math.max(min, Math.min(max, value));
    };
    return {
      x: clampAxis(nextPan.x, contentW, rect.width),
      y: clampAxis(nextPan.y, contentH, rect.height),
    };
  }, []);

  const toGridCoords = useCallback((clientX: number, clientY: number) => {
    if (!containerRef.current) return { gridX: 0, gridY: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    const x = (clientX - rect.left - livePanRef.current.x) / cellPx;
    const y = (clientY - rect.top - livePanRef.current.y) / cellPx;
    return { gridX: Math.floor(x), gridY: Math.floor(y) };
  }, [cellPx]);

  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();

    // Refs'ten oku → arka arkaya gelen olaylar bile birikerek doğru hesaplanır
    const currentZoom = zoomRef.current;
    const currentPan = panRef.current;

    // Yumuşak adım: trackpad gibi küçük deltaY için minik adım,
    // mouse wheel için ~1.1 faktör (deltaY≈100). Aşırı değerleri kırp.
    const dy = Math.max(-200, Math.min(200, e.deltaY));
    const factor = Math.exp(-dy * 0.001);
    const rawNewZoom = currentZoom * factor;
    const newZoom = +Math.max(minZoom, Math.min(maxZoom, rawNewZoom)).toFixed(3);
    if (Math.abs(newZoom - currentZoom) < 0.001) return;

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) { onZoomChange(newZoom); return; }

    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const scale = newZoom / currentZoom;
    const rawPan = {
      x: cx - (cx - currentPan.x) * scale,
      y: cy - (cy - currentPan.y) * scale,
    };
    const newTotalW = warehouseWidth * gridSize * newZoom;
    const newTotalH = warehouseHeight * gridSize * newZoom;
    const finalPan = clampPan(rawPan, newTotalW, newTotalH);

    // Refleri senkron güncelle — bir sonraki wheel olayı bu değerden devam eder
    zoomRef.current = newZoom;
    panRef.current = finalPan;

    onZoomChange(newZoom, finalPan);
  }, [onZoomChange, clampPan, warehouseWidth, gridSize, minZoom, maxZoom]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // Boşluk tuşu ile pan modu (Figma stili)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) return;
      if (e.code === 'Space' && !spaceHeld.current) {
        spaceHeld.current = true;
        setIsHoverPanZone(true);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        spaceHeld.current = false;
        if (!isPanning.current) setIsHoverPanZone(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  const startPan = (clientX: number, clientY: number) => {
    isPanning.current = true;
    panStart.current = { x: clientX, y: clientY };
    panOrigin.current = { ...livePanRef.current };
    setIsActivePan(true);
  };

  // Sağ/Orta tık + Alt/Space + Sol tık = pan. Capture fazında alıyoruz ki
  // alt elementlerin (Zone/Aisle/Platform vb.) stopPropagation'ı bizi engellemesin.
  const handleMouseDownCapture = (e: React.MouseEvent) => {
    const isPanTrigger =
      e.button === 1 ||
      e.button === 2 ||
      (e.button === 0 && (e.altKey || spaceHeld.current));
    if (!isPanTrigger) return;
    e.stopPropagation();
    e.preventDefault();
    startPan(e.clientX, e.clientY);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    // Pan zaten capture aşamasında tetiklendi; buraya yalnızca normal sol tık gelir
    const { gridX, gridY } = toGridCoords(e.clientX, e.clientY);
    onMouseDown(e, gridX, gridY);
  };

  const handleMouseMoveCapture = (e: React.MouseEvent) => {
    if (!isPanning.current) return;
    e.stopPropagation();
    const next = clampPan({
      x: panOrigin.current.x + (e.clientX - panStart.current.x),
      y: panOrigin.current.y + (e.clientY - panStart.current.y),
    }, totalW, totalH);
    livePanRef.current = next;
    setLivePan(next);
    panRef.current = next;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning.current) return; // capture'da işlendi
    const { gridX, gridY } = toGridCoords(e.clientX, e.clientY);
    onMouseMove(e, gridX, gridY);
  };

  const handleMouseUpCapture = (e: React.MouseEvent) => {
    if (!isPanning.current) return;
    e.stopPropagation();
    isPanning.current = false;
    setIsActivePan(false);
    if (!spaceHeld.current) setIsHoverPanZone(false);
    onPanChange(livePanRef.current);
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (isPanning.current) {
      isPanning.current = false;
      setIsActivePan(false);
      if (!spaceHeld.current) setIsHoverPanZone(false);
      onPanChange(livePanRef.current);
      return;
    }
    onMouseUp(e);
  };

  // Izgara arka planı da temel hücre boyutuyla çizilir (transform ölçekler).
  const gridBg = showGrid
    ? `repeating-linear-gradient(0deg, transparent, transparent ${gridSize - 1}px, rgba(0,0,0,0.06) ${gridSize - 1}px, rgba(0,0,0,0.06) ${gridSize}px), repeating-linear-gradient(90deg, transparent, transparent ${gridSize - 1}px, rgba(0,0,0,0.06) ${gridSize - 1}px, rgba(0,0,0,0.06) ${gridSize}px)`
    : undefined;

  // Cursor mantığı: pan modunda grab/grabbing, normal halde default
  const cursor = isActivePan ? 'grabbing' : isHoverPanZone ? 'grab' : 'default';

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        overflow: 'hidden',
        position: 'relative',
        bgcolor: '#f8f9fa',
        cursor,
        userSelect: 'none',
        // arka plan deseni - boş zeminin "tıklanabilir alan" hissini güçlendirir
        backgroundImage: 'radial-gradient(circle, rgba(0,0,0,0.04) 1px, transparent 1px)',
        backgroundSize: '16px 16px',
      }}
      onMouseDownCapture={handleMouseDownCapture}
      onMouseMoveCapture={handleMouseMoveCapture}
      onMouseUpCapture={handleMouseUpCapture}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onContextMenu={(e) => e.preventDefault()}
      onAuxClick={(e) => e.preventDefault()}
    >
      {/* Pan + zoom katmanı — transform GPU'da uygulanır (inline style: her frame Emotion class
          üretmesin diye). transformOrigin 0,0: pan/zoom matematiğiyle birebir uyumlu. */}
      <Box
        style={{ transform: `translate3d(${livePan.x}px, ${livePan.y}px, 0) scale(${zoom})` }}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: baseW,
          height: baseH,
          transformOrigin: '0 0',
          willChange: 'transform',
        }}>
        {/* Depo sınırı */}
        <Box sx={{
          position: 'absolute',
          left: 0, top: 0,
          width: baseW,
          height: baseH,
          border: '2px solid #333',
          borderRadius: '2px',
          background: gridBg,
          backgroundSize: showGrid ? `${gridSize}px ${gridSize}px` : undefined,
          bgcolor: showGrid ? undefined : '#fff',
          boxSizing: 'border-box',
          boxShadow: '0 4px 18px rgba(0,0,0,0.06)',
        }} />

        {children}
      </Box>
    </Box>
  );
};

export default MapGrid;
